// src/components/dashboard/PixCheckoutAsaas.jsx
// src/components/dashboard/PixCheckoutAsaas.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Heading,
    Text,
    VStack,
    HStack,
    Image,
    Flex,
    Input,
    Spinner,
    useToast,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
} from '@chakra-ui/react';
import { FiDollarSign, FiCopy, FiCheckCircle, FiAlertCircle, FiUser } from 'react-icons/fi';
import { supabase } from '../../config/supabaseClient';

const PixCheckoutAsaas = ({ userId, selectedPackage, validCoupon, couponCode, packages, onSuccess, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [pixCode, setPixCode] = useState('');
    const [pixImage, setPixImage] = useState('');
    const [paymentId, setPaymentId] = useState('');
    const [checkingPayment, setCheckingPayment] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');
    const [debugInfo, setDebugInfo] = useState(null); // Para debug
    const [profileData, setProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const toast = useToast();

    // Carregar perfil do usuário ao iniciar
    useEffect(() => {
        const fetchProfile = async () => {
            setProfileLoading(true);
            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', userId)
                    .single();

                if (error) {
                    throw error;
                }
                
                setProfileData(data);
                
                // Verificar se o perfil tem CPF/CNPJ
                if (!data.cpf_cnpj) {
                    setError('É necessário cadastrar seu CPF/CNPJ no perfil para realizar pagamentos');
                }
            } catch (err) {
                console.error('Erro ao carregar perfil:', err);
                setError('Erro ao carregar informações do perfil');
            } finally {
                setProfileLoading(false);
            }
        };

        fetchProfile();
    }, [userId]);

    const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);

    // Calcular preço com desconto
    const calculatePrice = () => {
        if (!selectedPkg) return 0;
        const basePrice = selectedPkg.price;
        return validCoupon ? basePrice * 0.85 : basePrice; // 15% de desconto
    };

    const handleGeneratePixQRCode = async () => {
        setLoading(true);
        setError('');
        setDebugInfo(null);

        try {
            // Verificar se tem perfil com CPF/CNPJ
            if (!profileData || !profileData.cpf_cnpj) {
                throw new Error('É necessário cadastrar seu CPF/CNPJ no perfil para realizar pagamentos');
            }

            // Limpar CPF/CNPJ (remover pontuações)
            const cleanCpfCnpj = profileData.cpf_cnpj.replace(/\D/g, '');
            if (cleanCpfCnpj.length !== 11 && cleanCpfCnpj.length !== 14) {
                throw new Error('CPF/CNPJ inválido. Por favor, atualize seu perfil com um CPF/CNPJ válido.');
            }

            // Obter sessão
            const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
            if (sessionError) throw new Error(`Erro de sessão: ${sessionError.message}`);

            const session = sessionData.session;
            if (!session) {
                throw new Error('Usuário não está autenticado. Faça login novamente.');
            }

            const authToken = session.access_token;

            // Registrar para debug
            setDebugInfo(prev => ({
                ...prev,
                auth: `Autenticado: ${!!authToken}`,
                token_preview: authToken ? `${authToken.substring(0, 10)}...` : 'Nenhum token',
                profile_data: JSON.stringify({
                    name: profileData.name,
                    email: profileData.email,
                    phone: profileData.phone,
                    cpf_cnpj: cleanCpfCnpj
                }, null, 2)
            }));

            // Preparar dados para envio
            const requestData = {
                amount: calculatePrice(),
                currency: 'brl',
                package_id: selectedPackage,
                credits: selectedPkg.credits,
                coupon_code: validCoupon ? couponCode : null,
                coupon_discount: validCoupon ? 15 : 0,
                customer: {
                    name: profileData.name || 'Cliente',
                    email: profileData.email,
                    phone: profileData.phone || '',
                    cpfCnpj: cleanCpfCnpj
                }
            };

            setDebugInfo(prev => ({
                ...prev,
                request_data: JSON.stringify(requestData, null, 2)
            }));

            // Chamar a Edge Function para gerar o PIX
            const response = await fetch('https://mlliabdepyirxtaytcco.functions.supabase.co/create-asaas-pix', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(requestData),
            });

            // Registrar resposta para debug
            setDebugInfo(prev => ({
                ...prev,
                response_status: response.status,
                response_ok: response.ok
            }));

            // Processar resposta
            const responseData = await response.json();

            setDebugInfo(prev => ({
                ...prev,
                response_data: JSON.stringify(responseData, null, 2)
            }));

            if (responseData.error) {
                throw new Error(responseData.error);
            }

            // Atualizar o estado com os dados do PIX
            setPixCode(responseData.pixCopiaECola);
            setPixImage(responseData.pixImageUrl);
            setPaymentId(responseData.paymentId);

            // Iniciar verificação de pagamento em background
            if (responseData.paymentId) {
                startPaymentCheck(responseData.paymentId);
            }

        } catch (error) {
            console.error('Erro ao gerar PIX:', error);
            setError(error.message || 'Ocorreu um erro ao gerar o código PIX.');

            toast({
                title: 'Erro ao gerar PIX',
                description: error.message || 'Ocorreu um erro ao gerar o código PIX.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const startPaymentCheck = (pid) => {
        setCheckingPayment(true);

        // Verificar a cada 5 segundos se o pagamento foi confirmado
        const checkInterval = setInterval(async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (!session) {
                    clearInterval(checkInterval);
                    setCheckingPayment(false);
                    return;
                }

                const response = await fetch(`https://mlliabdepyirxtaytcco.functions.supabase.co/check-payment-status?payment_id=${pid}`, {
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`
                    }
                });

                const data = await response.json();

                if (data.status === 'paid') {
                    clearInterval(checkInterval);
                    setCheckingPayment(false);
                    handlePaymentConfirmed();
                }
            } catch (error) {
                console.error('Erro ao verificar status do pagamento:', error);
            }
        }, 5000);

        // Limpar o intervalo após 15 minutos (tempo máximo de espera)
        setTimeout(() => {
            clearInterval(checkInterval);
            setCheckingPayment(false);
        }, 15 * 60 * 1000);
    };

    const handlePaymentConfirmed = async () => {
        toast({
            title: 'Pagamento confirmado!',
            description: `${selectedPkg.credits} créditos foram adicionados à sua conta.`,
            status: 'success',
            duration: 5000,
            isClosable: true,
        });

        if (onSuccess) onSuccess();
        onClose();
    };

    const handleCopyPixCode = () => {
        navigator.clipboard.writeText(pixCode);
        setCopied(true);

        toast({
            title: 'Código copiado!',
            description: 'O código PIX foi copiado para sua área de transferência.',
            status: 'success',
            duration: 2000,
            isClosable: true,
        });

        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <VStack spacing={6}>
            {/* Resumo da compra */}
            <Box p={4} bg="gray.50" borderRadius="md" width="100%">
                <Heading size="md" mb={2} color="gray.700">
                    Resumo
                </Heading>

                <HStack justify="space-between" mb={2}>
                    <Text>Pacote:</Text>
                    <Text fontWeight="medium">{selectedPkg?.description}</Text>
                </HStack>

                <HStack justify="space-between" mb={2}>
                    <Text>Valor original:</Text>
                    <Text>R$ {selectedPkg?.price.toFixed(2)}</Text>
                </HStack>

                {validCoupon && (
                    <HStack justify="space-between" mb={2}>
                        <Text>Desconto (15%):</Text>
                        <Text color="green.500">-R$ {(selectedPkg?.price * 0.15).toFixed(2)}</Text>
                    </HStack>
                )}

                <HStack justify="space-between" mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200">
                    <Text fontWeight="bold">Total:</Text>
                    <Text fontWeight="bold" color="brand.primary" fontSize="xl">
                        R$ {calculatePrice().toFixed(2)}
                    </Text>
                </HStack>
            </Box>

            {/* Informações do PIX */}
            <Box p={4} borderWidth="1px" borderRadius="md" width="100%">
                <VStack spacing={4} align="stretch">
                    <Heading size="sm" mb={2}>
                        Pagamento via PIX
                    </Heading>

                    {!pixCode ? (
                        <Button
                            onClick={handleGeneratePixQRCode}
                            colorScheme="green"
                            isLoading={loading}
                            leftIcon={<FiDollarSign />}
                            size="lg"
                            width="full"
                        >
                            Gerar QR Code PIX
                        </Button>
                    ) : (
                        <VStack spacing={4} align="center">
                            <Text fontSize="sm" textAlign="center">
                                Escaneie o QR Code abaixo ou copie o código PIX para realizar o pagamento.
                                O sistema irá verificar automaticamente quando o pagamento for confirmado.
                            </Text>

                            {pixImage && (
                                <Box
                                    p={4}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    borderColor="gray.200"
                                >
                                    <Image
                                        src={pixImage}
                                        alt="QR Code PIX"
                                        boxSize="200px"
                                        mx="auto"
                                    />
                                </Box>
                            )}

                            <Box p={3} bg="green.50" borderRadius="md" width="100%">
                                <Text fontWeight="medium" mb={2}>
                                    Código PIX:
                                </Text>
                                <Flex>
                                    <Input
                                        value={pixCode}
                                        isReadOnly
                                        bg="white"
                                        borderRightRadius={0}
                                    />
                                    <Button
                                        onClick={handleCopyPixCode}
                                        colorScheme="green"
                                        borderLeftRadius={0}
                                        leftIcon={copied ? <FiCheckCircle /> : <FiCopy />}
                                    >
                                        {copied ? "Copiado" : "Copiar"}
                                    </Button>
                                </Flex>
                            </Box>

                            {checkingPayment && (
                                <Box
                                    p={4}
                                    borderRadius="md"
                                    bg="blue.50"
                                    width="100%"
                                >
                                    <HStack spacing={3} align="center" justify="center">
                                        <Spinner color="blue.500" />
                                        <Text>
                                            Aguardando confirmação do pagamento...
                                        </Text>
                                    </HStack>
                                </Box>
                            )}

                            <Text fontSize="sm" color="gray.600">
                                O pagamento deve ser confirmado em até 15 minutos. Não feche esta janela.
                            </Text>
                        </VStack>
                    )}

                    {error && (
                        <Box p={3} bg="red.50" borderRadius="md" width="100%">
                            <HStack>
                                <FiAlertCircle color="red" />
                                <Text color="red.500" fontSize="sm">
                                    {error}
                                </Text>
                            </HStack>
                        </Box>
                    )}

                    {/* Área de debug - mostrar apenas em desenvolvimento 
                    {process.env.NODE_ENV === 'development' && debugInfo && (
                        <Box p={3} mt={4} bg="gray.50" borderRadius="md" borderWidth="1px">
                            <Heading size="xs" mb={2}>Debug Info</Heading>
                            <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {JSON.stringify(debugInfo, null, 2)}
                            </pre>
                        </Box>
                    )}
                    */}
                </VStack>
            </Box>
        </VStack>
    );
};

export default PixCheckoutAsaas;